import {useMemo} from 'react'

import {filterConversationComments} from './utils/filter-items'

import {usePullRequestBasePath} from '../../stores/layout-selectors'
import {sortConversationCommentsByDate} from './utils/sort-items'
import {CommentsSort} from './use-comments-sort-state'
import type {CommentsFilterState} from './use-comments-filter-state'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import type {ConversationComment} from '../../page-data/payloads/conversation-comments'

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
  /** Current sort direction. */
  sort: CommentsSort
}

const emptyItems: ConversationComment[] = []

/** Fetch and calculate filtered conversation comments (issue comments and review summaries). */
export function useFilteredConversationComments({filterState, sort}: UseFilteredCommentsDataOptions) {
  const basePath = usePullRequestBasePath()

  const query = useInfiniteConversationCommentsQuery({
    basePath,
  })
  const allComments = query.data?.comments ?? emptyItems

  // Conversation mode displays full list of comments and reviews with threads
  const filteredComments = useMemo(() => {
    // Diff-order sort is not supported for conversation mode
    if (sort === 'diff-order') return emptyItems

    const filteredSortedItems = sortConversationCommentsByDate(
      filterConversationComments(allComments, filterState),
      sort,
    )

    return filteredSortedItems
  }, [filterState, allComments, sort])

  return {
    query,
    /** Conversation comments matching the conversation filter and sort direction. */
    filteredComments,
    /** Total number of conversation comments in this PR. */
    totalConversationCommentsCount: allComments?.length ?? 0,
  }
}
